import { useEffect, useCallback, useRef } from 'react';

// Keep track of how many dialogs are open across the app
let openDialogCount = 0;

/**
 * A modal stack management hook to ensure proper cleanup of modals, popovers, dropdowns 
 * and prevent backdrop issues when closing nested overlays.
 */
export const useModalStack = (isOpen: boolean, onClose: () => void) => {
  const isMounted = useRef(false);
  const cleanupTimeoutRef = useRef<number | null>(null);
  const isClosingRef = useRef(false);
  const wasOpenRef = useRef(false);

  // Track mount status to prevent memory leaks
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      
      // Clear any pending timeouts when unmounting
      if (cleanupTimeoutRef.current !== null) {
        window.clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }

      // If component unmounts while open, decrement counter and restore scrolling if needed
      if (wasOpenRef.current) {
        openDialogCount = Math.max(0, openDialogCount - 1);
        if (openDialogCount === 0) {
          document.body.classList.remove('overflow-hidden');
          document.body.style.removeProperty('overflow');
        }
      }
    };
  }, []);

  // Keep track of open/close state for cleanup
  useEffect(() => {
    if (isOpen) {
      wasOpenRef.current = true;
    }
  }, [isOpen]);

  // Emit dialog events when the modal opens or closes
  useEffect(() => {
    if (isOpen) {
      // Notify that a dialog has opened
      document.dispatchEvent(new CustomEvent('dialogOpen'));
    } else if (isMounted.current && wasOpenRef.current) {
      // Only dispatch close event if component was previously open
      document.dispatchEvent(new CustomEvent('dialogClose'));
    }
  }, [isOpen]);

  // When this modal is opened, ensure body scrolling is disabled
  // When closed, restore body scrolling if no other modals are open
  useEffect(() => {
    if (isOpen && !isClosingRef.current) {
      // Increment counter when a dialog opens
      openDialogCount++;
      
      // Use classList for better compatibility and to avoid overriding inline styles
      document.body.classList.add('overflow-hidden');
      
      // Close any open dropdowns when a dialog opens
      document.dispatchEvent(new CustomEvent('dropdownMenuCloseRequest'));
      document.dispatchEvent(new CustomEvent('hoverCardCloseRequest'));
      
      return () => {
        if (cleanupTimeoutRef.current !== null) {
          window.clearTimeout(cleanupTimeoutRef.current);
          cleanupTimeoutRef.current = null;
        }
        
        cleanupTimeoutRef.current = window.setTimeout(() => {
          if (isMounted.current) {
            // Decrement counter when dialog closes
            openDialogCount = Math.max(0, openDialogCount - 1);
            
            // Only restore scrolling if no dialogs are open
            if (openDialogCount === 0) {
              document.body.classList.remove('overflow-hidden');
              document.body.style.removeProperty('overflow');
              
              // Force scroll position reset to fix stuck scrolling
              window.scrollTo({
                top: window.scrollY,
                behavior: 'auto'
              });
            }
            
            cleanupTimeoutRef.current = null;
          }
        }, 150);
      };
    }
  }, [isOpen]);

  // Return a handler for open state changes
  const handleOpenChange = useCallback((open: boolean) => {
    if (!open && isMounted.current && !isClosingRef.current) {
      isClosingRef.current = true;
      
      // Close any open dropdowns first
      document.dispatchEvent(new CustomEvent('dropdownMenuCloseRequest'));
      document.dispatchEvent(new CustomEvent('hoverCardCloseRequest'));
      
      // Slight delay to let animations complete
      setTimeout(() => {
        if (isMounted.current) {
          onClose();
          isClosingRef.current = false;
        }
      }, 50);
    }
  }, [onClose]);

  return { handleOpenChange };
};
